import { useCallback, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";

import { executeRelay, isUserRejection, RelayEventConfirmation, RelayResult } from "../services/relay.service";

// Shared state machine for gasless relay calls so every role screen (verifier, attestor,
// holder) behaves identically: sign → posted → event-confirmed / decoded-revert.
//
//   idle → signing     one MetaMask typed-data prompt (no gas, no transaction)
//        → relaying    POSTed to the backend, waiting for { txHash }
//        → confirming  tx mined + expected event seen on the read-only provider
//        → success | error
//
// "relaying"/"confirming" are NOT success — EcoForwarder.execute doesn't bubble inner
// reverts, so nothing may be reported done until the confirmation event is observed
// (or, with no confirmation configured, until the backend's success:true comes back).

export type RelayStatus = "idle" | "signing" | "relaying" | "confirming" | "success" | "error";

export interface RelayActionParams {
  // Target contract address (Admin / Core / Bank — must be on the backend's allowlist).
  to: string;
  iface: ethers.Interface;
  fn: string;
  args: any[];
  // Event that proves the inner call took effect (e.g. DeedAttested on Bank).
  // Strongly recommended — without it success means only "backend reported success".
  confirmation?: RelayEventConfirmation;
  // Human-readable action shown by the relay status UI while the user signs.
  label?: string;
}

export const useRelayAction = () => {
  const [status, setStatus] = useState<RelayStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  // Screens navigate away while a relay is in flight — don't setState after unmount.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const safeSet = useCallback(<T,>(setter: (v: T) => void, value: T) => {
    if (mounted.current) {setter(value);}
  }, []);

  const run = useCallback(
    async (params: RelayActionParams): Promise<RelayResult | null> => {
      safeSet(setError, null);
      safeSet(setTxHash, null);
      try {
        const result = await executeRelay({
          ...params,
          onPhase: (phase) => safeSet(setStatus, phase),
        });
        safeSet(setTxHash, result.txHash);
        safeSet(setStatus, "success");
        return result;
      } catch (e: any) {
        // MetaMask user rejection (4001 / ACTION_REJECTED) — back to idle, not an error state.
        if (isUserRejection(e)) {
          safeSet(setStatus, "idle");
          return null;
        }
        safeSet(setError, e?.message ?? "Relay action failed");
        safeSet(setStatus, "error");
        return null;
      }
    },
    [safeSet]
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
    setTxHash(null);
  }, []);

  const isPending = status === "signing" || status === "relaying" || status === "confirming";

  return { run, reset, status, error, txHash, isPending };
};

export default useRelayAction;
